/**
 * All money in this system is ETB, stored as a whole number of birr.
 * There are no decimals anywhere: not in Firestore, not in arithmetic,
 * not on screen. Santim are not tracked because the committee does not
 * collect them.
 */

export type Birr = number;

const GROUPING = /\B(?=(\d{3})+(?!\d))/g;

export class MoneyError extends Error {}

/** Throws unless the value is a finite whole number of birr. */
export function assertBirr(value: number, label = "amount"): asserts value is Birr {
  if (!Number.isFinite(value)) {
    throw new MoneyError(`${label} must be a finite number, received ${value}`);
  }
  if (!Number.isInteger(value)) {
    throw new MoneyError(`${label} must be a whole number of birr, received ${value}`);
  }
}

/** "2000" -> "2,000". Negative values keep the sign ahead of the digits. */
export function groupDigits(value: Birr): string {
  assertBirr(value);
  const negative = value < 0;
  const digits = String(Math.abs(value)).replace(GROUPING, ",");
  return negative ? `-${digits}` : digits;
}

/**
 * "2,000 birr".
 *
 * It used to read "ETB 2,000". ETB is the code a bank puts on a wire transfer.
 * It is not a word anybody in Addis says out loud, and a reader who has not met
 * it before gets three letters that are not a word before they get to the
 * number. Since this is the most repeated string in the product, that was the
 * most repeated confusion in the product.
 *
 * The word follows the number now, which is how it is said in both languages:
 * "2,000 birr", "2,000 ብር".
 */
export function formatBirr(value: Birr, script: "am" | "latin" = "latin"): string {
  return `${groupDigits(value)} ${script === "am" ? "ብር" : "birr"}`;
}

/**
 * Splits an amount into the largest whole number of units it covers and
 * whatever is left over. Underpins the settlement rule: a payment settles
 * whole months only, and any remainder becomes held funds rather than a
 * credit against future rent.
 */
export function divideIntoUnits(amount: Birr, unitPrice: Birr): { units: number; remainder: Birr } {
  assertBirr(amount, "amount");
  assertBirr(unitPrice, "unitPrice");
  if (unitPrice <= 0) {
    throw new MoneyError(`unitPrice must be positive, received ${unitPrice}`);
  }
  if (amount < 0) {
    throw new MoneyError(`amount must not be negative, received ${amount}`);
  }
  return {
    units: Math.floor(amount / unitPrice),
    remainder: amount % unitPrice,
  };
}
